import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DocumentDto } from '@tww/shared';
import { fmtDate, fmtNum } from '@tww/shared';
import { Icon } from './Icon';

/** Dateityp aus Name und MIME: Farbe und Kurzzeichen für das Icon, Vorschau-Art */
export type FileKind = 'pdf' | 'word' | 'excel' | 'ppt' | 'image' | 'video' | 'audio' | 'text' | 'zip' | 'mail' | 'file';
export function fileKind(d: { original_name: string; mime: string | null }): FileKind {
  const ext = d.original_name.toLowerCase().split('.').pop() ?? '';
  const m = d.mime ?? '';
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (m.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg'].includes(ext)) return 'image';
  if (m.startsWith('video/') || ['mp4', 'mov', 'webm', 'm4v'].includes(ext)) return 'video';
  if (m.startsWith('audio/') || ['mp3', 'wav', 'm4a'].includes(ext)) return 'audio';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'xlsm', 'ods', 'csv'].includes(ext)) return 'excel';
  if (['ppt', 'pptx', 'odp'].includes(ext)) return 'ppt';
  if (['txt', 'md', 'json', 'xml', 'log'].includes(ext) || m.startsWith('text/')) return 'text';
  if (['zip', 'rar', '7z', 'gz'].includes(ext)) return 'zip';
  if (['eml', 'msg'].includes(ext)) return 'mail';
  return 'file';
}
const KIND_LABEL: Record<FileKind, string> = { pdf: 'PDF', word: 'DOC', excel: 'XLS', ppt: 'PPT', image: 'IMG', video: 'VID', audio: 'AUD', text: 'TXT', zip: 'ZIP', mail: 'MAIL', file: '' };
export const KIND_NAME: Record<FileKind, string> = { pdf: 'PDF', word: 'Word-Dokument', excel: 'Tabelle', ppt: 'Präsentation', image: 'Bild', video: 'Video', audio: 'Audio', text: 'Text', zip: 'Archiv', mail: 'E-Mail', file: 'Datei' };

/** Icon mit Kurzzeichen des Dateityps (PDF rot, Word blau, Excel grün, …); Bilder als Miniatur */
export function FileIcon({ d, size = 36 }: { d: DocumentDto; size?: number }) {
  const k = fileKind(d);
  if (d.has_thumb) return <img className="thumb" src={`/api/v1/documents/${d.id}/thumb`} alt="" style={{ width: size, height: size }} />;
  const ext = (d.original_name.split('.').pop() ?? '').toUpperCase().slice(0, 4);
  return (
    <span className={`ficon ${k}`} style={{ width: size, height: size }} aria-label={KIND_NAME[k]}>
      {KIND_LABEL[k] || ext || <Icon name="doc" />}
    </span>
  );
}

export const canPreview = (d: DocumentDto) => ['pdf', 'image', 'video', 'audio', 'word', 'excel', 'text'].includes(fileKind(d));

/**
 * Vorschau als Overlay: PDF und Text im Frame, Bilder, Video und Audio nativ, Word über docx-preview,
 * Excel über SheetJS als Tabellen je Blatt. Immer mit Download, Blättern in der Liste, Escape schließt.
 */
export function FilePreview({ doc, list, onClose, onNavigate, extra }: { doc: DocumentDto; list?: DocumentDto[]; onClose: () => void; onNavigate?: (d: DocumentDto) => void; extra?: React.ReactNode }) {
  const k = fileKind(doc);
  const url = `/api/v1/documents/${doc.id}/file`;
  const idx = list ? list.findIndex((x) => x.id === doc.id) : -1;
  const prev = list && idx > 0 ? list[idx - 1] : null;
  const next = list && idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && prev && onNavigate) onNavigate(prev);
      if (e.key === 'ArrowRight' && next && onNavigate) onNavigate(next);
    };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose, onNavigate, prev, next]);
  return createPortal(
    <div className="fp-bg" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fp" role="dialog" aria-modal="true" aria-label={doc.title || doc.original_name}>
        <div className="fp-head">
          <FileIcon d={{ ...doc, has_thumb: false }} size={28} />
          <div className="fp-title">
            <strong>{doc.title || doc.original_name}</strong>
            <span>
              {[KIND_NAME[k], fmtDate(doc.taken_at ?? doc.created_at), doc.size_bytes ? `${fmtNum(Math.round(doc.size_bytes / 1024))} KB` : null, doc.created_by_name].filter(Boolean).join(' · ')}
            </span>
          </div>
          {extra}
          {list && list.length > 1 && (
            <span className="fp-nav">
              <button type="button" className="icon-btn" disabled={!prev} onClick={() => prev && onNavigate?.(prev)} aria-label="Vorherige Datei">
                ‹
              </button>
              <span className="mut small">
                {idx + 1} / {list.length}
              </span>
              <button type="button" className="icon-btn" disabled={!next} onClick={() => next && onNavigate?.(next)} aria-label="Nächste Datei">
                ›
              </button>
            </span>
          )}
          <a className="btn sm" href={`${url}?download=1`}>
            <Icon name="download" /> Herunterladen
          </a>
          <a className="btn sm" href={url} target="_blank" rel="noreferrer">
            <Icon name="external" /> Neuer Tab
          </a>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
            <Icon name="x" />
          </button>
        </div>
        <div className="fp-body">
          {k === 'image' && <img src={url} alt={doc.original_name} />}
          {k === 'pdf' && <iframe src={`${url}#toolbar=1`} title={doc.original_name} />}
          {k === 'text' && <iframe src={url} title={doc.original_name} className="text" />}
          {k === 'video' && <video src={url} controls autoPlay={false} />}
          {k === 'audio' && <audio src={url} controls />}
          {k === 'word' && <DocxView url={url} />}
          {k === 'excel' && <SheetView url={url} />}
          {!canPreview(doc) && (
            <div className="fp-none">
              <FileIcon d={{ ...doc, has_thumb: false }} size={64} />
              <p>Für diesen Dateityp gibt es keine Vorschau.</p>
              <a className="btn primary" href={`${url}?download=1`}>
                <Icon name="download" /> Herunterladen
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DocxView({ url }: { url: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => {
    let alive = true;
    setState('loading');
    (async () => {
      try {
        const [{ renderAsync }, res] = await Promise.all([import('docx-preview'), fetch(url, { credentials: 'same-origin' })]);
        if (!res.ok) throw new Error(`Fehler ${res.status}`);
        const blob = await res.blob();
        if (!alive || !ref.current) return;
        ref.current.innerHTML = '';
        await renderAsync(blob, ref.current, undefined, { inWrapper: true, ignoreWidth: false, ignoreHeight: true, useBase64URL: true });
        if (alive) setState('ok');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return (
    <div className="fp-doc">
      {state === 'loading' && <div className="mut" style={{ padding: 24 }}>Dokument wird geladen …</div>}
      {state === 'error' && <div className="fp-none">Das Dokument kann im Browser nicht dargestellt werden (nur .docx). Bitte herunterladen.</div>}
      <div ref={ref} />
    </div>
  );
}

function SheetView({ url }: { url: string }) {
  const [sheets, setSheets] = useState<{ name: string; html: string }[] | null>(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [XLSX, res] = await Promise.all([import('xlsx'), fetch(url, { credentials: 'same-origin' })]);
        if (!res.ok) throw new Error(`Fehler ${res.status}`);
        const wb = XLSX.read(await res.arrayBuffer(), { type: 'array', cellDates: true });
        const out = wb.SheetNames.map((name) => ({ name, html: XLSX.utils.sheet_to_html(wb.Sheets[name]!, { header: '', footer: '' }) }));
        if (alive) setSheets(out);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  if (error) return <div className="fp-none">Die Tabelle kann im Browser nicht dargestellt werden. Bitte herunterladen.</div>;
  if (!sheets) return <div className="mut" style={{ padding: 24 }}>Tabelle wird geladen …</div>;
  return (
    <div className="fp-sheet">
      {sheets.length > 1 && (
        <div className="nav-tabs" style={{ padding: '0 12px' }}>
          {sheets.map((s, i) => (
            <button type="button" key={s.name} className={i === active ? 'active' : ''} onClick={() => setActive(i)}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="fp-sheet-body" dangerouslySetInnerHTML={{ __html: sheets[active]?.html ?? '' }} />
    </div>
  );
}
